import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ViewTypesNames } from "@redotech/redo-views-common/views";
import { useQueries } from "@tanstack/react-query";
import {
  recordViewCountsQueryKey,
  RecordViewType,
} from "./use-record-view-counts";

const VIEW_TYPE_FOR_RETURN_TYPE = {
  [ReturnTypeEnum.RETURN]: ViewTypesNames.RETURNS,
  [ReturnTypeEnum.CLAIM]: ViewTypesNames.CLAIMS,
  [ReturnTypeEnum.WARRANTY]: ViewTypesNames.WARRANTIES,
  [ReturnTypeEnum.RECYCLE]: ViewTypesNames.RECYCLE,
  [ReturnTypeEnum.WITHDRAWAL]: ViewTypesNames.WITHDRAWALS,
} satisfies Record<ReturnTypeEnum, RecordViewType>;

export interface RecordFilterCountRequest {
  /** Names the count in the result. */
  key: string;
  type: ReturnTypeEnum;
  filters: AdvancedFilterData[];
  /** The table's search text, such as a customer's name. */
  search?: string;
}

/**
 * How many records match each set of filters and search text, counted the
 * way the table counts them, in the merchant's timezone. The queries share
 * the view counts' key prefix, so whatever refreshes a type's view counts
 * refreshes these.
 */
export function useRecordFilterCounts(
  requests: readonly RecordFilterCountRequest[],
): { counts: Record<string, number | undefined>; isPending: boolean } {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const merchantTimezone = team.ianaTimezone;

  return useQueries({
    queries: requests.map(({ type, filters, search }) => ({
      queryKey: [
        ...recordViewCountsQueryKey(team._id, VIEW_TYPE_FOR_RETURN_TYPE[type]),
        { filters, search, merchantTimezone },
      ],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        (
          await rpcClient.getReturnsCount(
            { type, advancedFiltersData: filters, search, merchantTimezone },
            { signal },
          )
        ).total,
    })),
    combine: (results) => ({
      counts: Object.fromEntries(
        requests.map((request, index) => [request.key, results[index]?.data]),
      ),
      isPending: results.some((result) => result.isPending),
    }),
  });
}
